import qs from 'qs';

import {
  ListSopReturn,
  SlackSopSettings,
  Sop,
  SopRefreshCadenceType,
  SopStateType,
} from '~/support/services/types/sop.types';
import { StoreCollaborationStatus } from '@monolog/shared/types/database/identity-svc';

import GatewayBase from './gateway-base';

/** A developer-org collaborator request/relationship as seen by the merchant (includes org name). */
export interface StoreCollaborator {
  id: string;
  accountId: string;
  storeId: string;
  status: StoreCollaborationStatus;
  code: string;
  createdAt: string;
  account?: { id: string; name: string };
}

import type {
  GeneralSettingsType,
  StoreContactSettings,
  StoreCreateObjects,
  ABTest,
  BrandingSetting,
  StoreUpdateObjects,
  UpdateClaimAutomationSettingsDto,
} from './types/merchant.types';
import type { PaginatedResponse } from '../../../../../libs/shared/types';
import { Merchant } from '@database/types';
import {
  ClaimType,
  CoverageRule,
  EvidenceType,
  PolicyType,
  RiskRoutingAction,
  RiskRoutingConfig,
  StackingRule,
  YofiRiskTier,
} from '@monolog/shared/types/database/merchant-svc';

export type DisabledCapability = {
  key: string;
  message: string;
};

export type Capabilities = {
  claimAutomation: boolean;
  claimSubtypes: boolean;
  credit: boolean;
  /** False when the capabilities read couldn't check the store's Shopify permissions. */
  creditEligibilityKnown?: boolean;
  customClaims: boolean;
  customEmails: boolean;
  customInfoModals: boolean;
  customWidgets: boolean;
  customizations: boolean;
  disabledCapabilities: DisabledCapability[];
  draftOrder: boolean;
  id?: string;
  inventory: boolean;
  refund: boolean;
  reship: boolean;
  returns: boolean;
  returnsIndex: boolean;
  sop: boolean;
  splitTest: boolean;
  storeId?: string;
  updatedAt?: string;
  warranties: boolean;
  manufacturerWarranties: boolean;
  extendedWarranties: boolean;
  warrantiesMetafieldsImport: boolean;
  offlineClaims: boolean;
  productSync: boolean;
};

export type OfflineClaimSettings = {
  id?: string;
  storeId?: string;
  warrantyClaimsEnabled: boolean;
  shippingClaimsEnabled: boolean;
  receiptRequired: boolean;
  // ENG-5871 — whether an uploaded receipt is read by a vision model to prefill the claim form.
  receiptPrefillEnabled: boolean;
  orderNumberOptional: boolean;
  externalSalesChannels: string[];
  // ENG-5873 — whether a claim whose item cannot be reshipped may be filled with a near-equivalent
  // product, and how far that replacement's price may sit from the original, as a percentage.
  allowCloseMatch: boolean;
  closeMatchMaxPriceIncreasePct: number;
  closeMatchMaxPriceDecreasePct: number;
  createdAt?: string;
  updatedAt?: string;
};

type TagSyncSettings = {
  syncWithPlatform: boolean;
  addDefaultOpTags: boolean;
  resolutionTypeTags: boolean;
  customOrderTags: boolean;
  customCustomerTags: boolean;
  claimStateTags: boolean;
  claimTypeTags: boolean;
  claimCategoryTags: boolean;
  pricePolicyTags: boolean;
  reshipCopyOrderTags: boolean;
};

export type CapabilitiesInput = Omit<
  Capabilities,
  'id' | 'storeId' | 'disabledCapabilities' | 'creditEligibilityKnown'
>;

export interface AdminStoreListItem {
  capabilities: object;
  generalSettings: object;
  storeSettings: object;
  contactSettings: object;
  claimAutomationSettings?: { model?: string | null; automationCapabilities?: Record<string, boolean> | null } | null;
  id: string;
  name: string;
  slug: string;
  accountId: string;
  platformId: string;
  storeDomain: string;
}

type ShopifyReinstallBody = {
  clientSecret: string;
  clientId: string;
  callerOrigin?: string;
};

type BigCommerceReinstallBody = {
  accessToken: string;
  clientId: string;
  clientSecret: string;
  apiPath: string;
};

type ReinstallBody = ShopifyReinstallBody | BigCommerceReinstallBody;

export interface CoverageTierInput {
  minAmount: number;
  maxAmount: number | null;
  reimbursementPct: number;
}

export interface CoverageEvidenceInput {
  type: EvidenceType;
  threshold: number | null;
}

export interface CoverageRuleInput {
  claimType: ClaimType;
  category: PolicyType;
  included: boolean;
  creditStore: boolean;
  evidence: CoverageEvidenceInput[];
  tiers: CoverageTierInput[];
}

export interface RiskRoutingRuleInput {
  tier: YofiRiskTier;
  action: RiskRoutingAction;
  reimbursementPct: number | null;
  escalate: boolean;
}

export interface RiskRoutingConfigInput {
  stackingRule: StackingRule;
  rules: RiskRoutingRuleInput[];
}

export class MerchantRestService extends GatewayBase {
  get coverageRules() {
    return {
      forStore: this.getCoverageRules,
      upsert: this.upsertCoverageRules,
    };
  }

  get riskRouting() {
    return {
      forStore: this.getRiskRouting,
      upsert: this.upsertRiskRouting,
    };
  }

  /** Merchant-side review of developer-org collaborator requests for a store. */
  get collaborations() {
    return {
      approve: this.approveCollaboration,
      decline: this.declineCollaboration,
      list: this.listStoreCollaborations,
      listPending: this.listPendingCollaborations,
      revoke: this.revokeCollaboration,
    };
  }

  private listPendingCollaborations = (storeId: string) => {
    return this.client.get<StoreCollaborator[]>(`/v1/stores/${storeId}/collaborations/pending`);
  };

  private listStoreCollaborations = (storeId: string) => {
    return this.client.get<StoreCollaborator[]>(`/v1/stores/${storeId}/collaborations`);
  };

  private approveCollaboration = (storeId: string, id: string) => {
    return this.client.post(`/v1/stores/${storeId}/collaborations/${id}/approve`);
  };

  private declineCollaboration = (storeId: string, id: string) => {
    return this.client.post(`/v1/stores/${storeId}/collaborations/${id}/decline`);
  };

  private revokeCollaboration = (storeId: string, id: string) => {
    return this.client.post(`/v1/stores/${storeId}/collaborations/${id}/revoke`);
  };

  private getCoverageRules = (storeId: string) => {
    return this.client.get<CoverageRule[]>(`/v1/coverage-rules/${storeId}`);
  };

  private upsertCoverageRules = (
    storeId: string,
    rules: CoverageRuleInput[],
    options: { includeOtasInReimbursements?: boolean } = {},
  ) => {
    return this.client.put<CoverageRule[]>(`/v1/coverage-rules/${storeId}`, { rules, ...options });
  };

  private getRiskRouting = (storeId: string) => {
    return this.client.get<RiskRoutingConfig | null>(`/v1/risk-routing/${storeId}`);
  };

  private upsertRiskRouting = (storeId: string, config: RiskRoutingConfigInput) => {
    return this.client.put<RiskRoutingConfig>(`/v1/risk-routing/${storeId}`, config);
  };

  get stores() {
    return {
      accountDashboard: this.getAccountDashboardUrl,
      activate: this.activateStore,
      admin: {
        list: this.getAdminStores,
        reinstall: this.reinstallStore,
      },
      branding: {
        get: this.getBrandingSettings,
        update: this.updateBrandingSettings,
      },
      capabilities: {
        get: this.getCapabilities,
        update: this.updateCapabilities,
      },
      childAccountDashboard: this.getChildAccountDashboardUrl,
      clearWidgetCache: this.clearWidgetCache,
      contact: {
        get: this.fetchContactSettings,
        update: this.updateContactSettings,
      },
      count: this.getStoreCounts,
      create: this.createStore,
      deactivate: this.moveToInactive,
      develop: this.markAsDevelopment,
      exchangeRate: {
        get: this.getMerchantOrderExchangeRate,
      },
      inventory: {
        get: this.getMerchantOrderInventory,
      },
      list: this.getStores,
      metabase: this.getMetabaseUrl,
      onboarding: this.markAsOnboarding,
      products: {
        byName: this.getProductByName,
      },
      settings: {
        claim: {
          get: this.getClaimSettings,
          update: this.updateClaimSettings,
        },
        claimAutomation: {
          get: this.getClaimAutomationSettings,
          update: this.updateClaimAutomationSettings,
        },
        coverageMode: {
          update: this.updateCoverageMode,
        },
        general: {
          get: this.getGeneralSettings,
          update: this.updateGeneralSettings,
        },
        global: {
          get: this.getStoreSettings,
          update: this.updateStoreSettings,
        },
        metadata: {
          get: this.getTagSettings,
          update: this.updateTagSettings,
        },
        offlineClaims: {
          get: this.getOfflineClaimSettings,
          update: this.updateOfflineClaimSettings,
        },
        warranty: {
          get: this.getWarranty,
          inStore: {
            get: this.getWarrantySettings,
            update: this.updateWarrantySettings,
          },
          update: this.updateWarranty,
          updateDefaults: this.updateDefaultWarrantyStatus,
        },
      },
      update: this.updateStore,
    };
  }

  get platforms() {
    return {
      cancelOrder: this.cancelOrder,
      deactivateDiscountCode: this.deactivateDiscountCode,
      deactivateGiftCard: this.deactivateGiftCard,
    };
  }

  get pricing() {
    return {
      create: this.createPriceRuleSet,
      rules: this.getRules,
      update: this.updatePriceRuleSet,
      variant: this.getClosestVariant,
    };
  }

  get tests() {
    return {
      create: this.createTest,
      duplicate: this.duplicateTest,
      end: this.endTest,
      get: this.getTest,
      list: this.getTests,
      start: this.startTest,
      update: this.updateTest,
      updateTestPriceRuleSet: this.updateTestPriceRuleSet,
      updateTestWidgetConfiguration: this.updateTestWidgetConfiguration,
    };
  }

  get sops() {
    return {
      create: this.createSop,
      delete: this.deleteSopById,
      get: this.getSopById,
      getDistillInstructions: this.getDistillInstructions,
      getHelpDeskSettings: this.getHelpDeskSettings,
      getSlackSettings: this.getSlackSopSettings,
      importGorgias: this.importGorgiasSops,
      list: this.listSopsByStoreId,
      provision: this.provisionStore,
      pullGorgiasTickets: this.pullGorgiasTicketMessages,
      pullSlackMessages: this.pullSlackMessages,
      update: this.updateSopById,
      updateHelpDeskSettings: this.updateHelpDeskSettings,
      updateSlackSettings: this.updateSlackSopSettings,
      updateState: this.updateSopState,
    };
  }
  private getProductByName = (storeId: string, name: string, orderId?: string, includeInStoreInventory = false) =>
    this.client.get<
      {
        id: unknown;
        productId: string | number;
        title: string;
        displayName: string;
        price: number;
        image: string;
        sku: string;
        selectedOptions: unknown[];
        inventoryQuantity: number;
        currency: string;
        parent: { productId: string };
      }[]
    >(`v1/platforms/${storeId}/products?name=${name}`, { params: { includeInStoreInventory, orderId } });

  private getBrandingSettings = (storeId: string) => {
    return this.client.get<BrandingSetting>(`v1/stores/${storeId}/whitelabel`);
  };

  private updateBrandingSettings = (storeId: string, body: Partial<BrandingSetting>) => {
    return this.client.put<BrandingSetting>(`v1/stores/${storeId}/whitelabel`, body);
  };

  private getCapabilities = (storeId: string) => {
    return this.client.get<Capabilities>(`v1/stores/${storeId}/capabilities`);
  };

  private updateCapabilities = (
    { storeId, storeDomain }: { storeId: string; storeDomain: string },
    input: CapabilitiesInput,
  ) => {
    return this.client.put<Capabilities>(`v1/stores/${storeId}/capabilities?storeDomain=${storeDomain}`, input);
  };

  private createTest = (storeId: string, input: any) => {
    return this.client.post(`/v1/stores/${storeId}/tests`, input);
  };

  private duplicateTest = (storeId: string, testId: string) => {
    return this.client.post<ABTest>(`/v1/stores/${storeId}/tests/${testId}/duplicate`);
  };

  private endTest = (storeId: string, testId: string) => {
    return this.client.post<ABTest>(`/v1/stores/${storeId}/tests/${testId}/end`);
  };

  private startTest = (storeId: string, testId: string) => {
    return this.client.post<ABTest>(`/v1/stores/${storeId}/tests/${testId}/start`);
  };

  private getTests = (storeId: string) => {
    return this.client.get<Omit<ABTest, 'results'>[]>(`/v1/stores/${storeId}/tests`);
  };

  private getTest = (storeId: string, testId: string) => {
    return this.client.get<ABTest>(`/v1/stores/${storeId}/tests/${testId}`);
  };

  private updateTest = (input: { storeId: string; testId: string; data: any }) => {
    return this.client.put<ABTest>(`/v1/stores/${input.storeId}/tests/${input.testId}`, input.data);
  };

  private updateTestPriceRuleSet = (input: {
    storeId: string;
    testId: string;
    testPriceRuleSetId: string;
    title?: string;
    weight?: number | null;
  }) => {
    const payload: { title?: string; weight?: number | null } = {};
    if (input.title !== undefined) payload.title = input.title;
    if (input.weight !== undefined) payload.weight = input.weight;

    return this.client.patch(
      `/v1/stores/${input.storeId}/tests/${input.testId}/test-price-rule-sets/${input.testPriceRuleSetId}`,
      payload,
    );
  };

  private updateTestWidgetConfiguration = (input: {
    storeId: string;
    testId: string;
    testWidgetConfigurationId: string;
    title?: string;
    widgetConfigurationId?: string;
  }) => {
    return this.client.patch(
      `/v1/stores/${input.storeId}/tests/${input.testId}/test-widget-configurations/${input.testWidgetConfigurationId}`,
      {
        ...(input.title && { title: input.title }),
        ...(input.widgetConfigurationId && { widgetConfigurationId: input.widgetConfigurationId }),
      },
    );
  };

  private getStoreSettings = (storeId: string) => {
    return this.client.get<{ storeLogoUrl: string; enableCoBranding: boolean; storeIndustry: string }>(
      `v1/stores/${storeId}/settings/global`,
    );
  };

  private updateStoreSettings = (storeId: string, body: any) => {
    return this.client.put(`v1/stores/${storeId}/settings/global`, body);
  };

  private getGeneralSettings = (storeId: string) => {
    return this.client.get<Merchant.GeneralSetting>(`v1/stores/${storeId}/settings/general`);
  };

  private updateGeneralSettings = ({ storeId, storeDomain }: { storeId: string; storeDomain: string }, body: any) => {
    return this.client.put(`v1/stores/${storeId}/settings/general?storeDomain=${storeDomain}`, body);
  };

  private updateCoverageMode = (
    { storeId, storeDomain }: { storeId: string; storeDomain: string },
    coverageMode: Merchant.CoverageMode,
  ) => {
    return this.client.put<Merchant.GeneralSetting>(
      `v1/stores/${storeId}/settings/coverage-mode?storeDomain=${storeDomain}`,
      { coverageMode },
    );
  };

  private getClaimSettings = (storeId: string) => {
    return this.client.get(`v1/stores/${storeId}/settings/claim`);
  };

  private updateClaimSettings = (storeId: string, body: any) => {
    return this.client.put(`v1/stores/${storeId}/settings/claim`, body);
  };

  private getClaimAutomationSettings = (storeId: string) => {
    return this.client.get(`v1/stores/${storeId}/claim-automation-settings`);
  };

  private updateClaimAutomationSettings = (storeId: string, body: UpdateClaimAutomationSettingsDto) => {
    return this.client.patch(`v1/stores/${storeId}/claim-automation-settings`, body);
  };

  private fetchContactSettings = (storeId: string) => {
    return this.client.get<StoreContactSettings>(`v1/stores/${storeId}/settings/contacts`);
  };

  private updateContactSettings = (storeId: string, body: unknown) => {
    return this.client.put(`v1/stores/${storeId}/settings/contacts`, body);
  };

  private clearWidgetCache = (storeDomain: string) => {
    return this.client.delete(`/v1/stores/widget/cache?storeDomain=${storeDomain}`);
  };

  private activateStore = (id: string) => {
    return this.client.post(`/v1/stores/${id}/activate`);
  };

  private markAsOnboarding = (id: string) => {
    return this.client.post(`/v1/stores/${id}/onboarding`);
  };

  private moveToInactive = (id: string) => {
    return this.client.post(`/v1/stores/${id}/deactivate`);
  };

  private markAsDevelopment = (id: string) => {
    return this.client.post(`/v1/stores/${id}/development`);
  };

  /** Signed parent-level rollup embed — every Child Account under this reseller (ENG-5850). */
  private getAccountDashboardUrl = () => {
    return this.client.get<string>('/v1/account-dashboards/account');
  };

  /** Signed child-level embed; Metabase owns the Child Account selector inside the iframe. */
  private getChildAccountDashboardUrl = () => {
    return this.client.get<string>('/v1/account-dashboards/child-account');
  };

  private getMetabaseUrl = (id: string) => {
    return this.client.get<string>(`/v1/stores/${id}/settings/metabase`);
  };

  private updateTagSettings = (storeId: string, body: Partial<TagSyncSettings>) => {
    return this.client.put(`/v1/tagging/${storeId}`, body);
  };

  private getTagSettings = (storeId: string) => {
    return this.client.get<{ storeId: string } & TagSyncSettings>(`/v1/tagging/${storeId}`);
  };

  private createStore = <T extends keyof StoreCreateObjects>(input: StoreCreateObjects[T]) => {
    return this.client.post<{
      id: string;
      name: string;
      slug: string;
      accountId: string;
      platformId: T;
      storeDomain: string;
    }>(`/v1/stores`, input);
  };

  private updateStore = (storeId: string, input: StoreUpdateObjects) => {
    return this.client.put<{ id: string }>(`/v1/stores/${storeId}`, input);
  };

  private getStores = (params: { select?: string[]; [k: string]: unknown } = {}) => {
    const { select, ...rest } = params;
    const queryParams: Record<string, unknown> = { ...rest };
    if (select?.length) {
      queryParams['select'] = select.join(',');
    }

    return this.client.get('/v1/stores', { params: queryParams });
  };

  private getAdminStores = (params: object = {}) => {
    return this.client.get<PaginatedResponse<AdminStoreListItem>>('/v1/admin/stores', {
      params,
      /*
       * The Parent Account filter sends `filter[accountId][in]` (ENG-5850 Surface D), and the paginator
       * spreads any filter key straight into the Prisma `where`. Axios's default serializer writes
       * arrays with explicit indices (`[in][0]=`), which Express's qs parser silently degrades to an
       * OBJECT past its `arrayLimit` of 20 — so a reseller with 22+ accounts in scope would send Prisma
       * `{ in: { '0': ... } }`. Bracket-push notation has no such limit.
       */
      paramsSerializer: value => qs.stringify(value, { arrayFormat: 'brackets' }),
    });
  };

  private reinstallStore = (storeId: string, body: ReinstallBody) => {
    return this.client.post<{ success: boolean; oauthUrl?: string }>(`/v1/admin/stores/${storeId}/reinstall`, body);
  };

  private getClosestVariant = (storeId: string, subtotal: number) => {
    return this.client.get(`v1/quotes/${storeId}/variant?subtotal=${subtotal}`);
  };

  private getRules = (storeId: string, type?: Merchant.PriceRuleSetType) => {
    return this.client.get<Merchant.PriceRuleSet[]>(`v1/quotes/${storeId}/rules`, { params: { type } });
  };

  private getStoreCounts = () => {
    return this.client.get<{
      active: number;
      inactive: number;
      onboarding: number;
      dev: number;
    }>('/v1/stores/counts');
  };

  private updatePriceRuleSet = (
    id: string,
    input: {
      storeId: string;
      storeDomain: string;
      payload: {
        rules?: {
          brandCalcMethod?: string;
          brandPrice?: number;
          customerCalcMethod?: string;
          customerPrice?: number;
          hybridOperator?: string;
          maxValue: number;
          minValue: number;
          pricingModel?: string;
        }[];
        automaticallyAddToCart?: boolean;
        noOp?: boolean;
      };
    },
  ) => {
    return this.client.put(`v1/quotes/${input.storeId}/rules/${id}?storeDomain=${input.storeDomain}`, input.payload);
  };

  private getMerchantOrderInventory = (storeId: string, sourceOrderId: string, isInStore: boolean) => {
    return this.client.get(`v1/merchant/${storeId}/inventory/${sourceOrderId}`, { params: { inStore: isInStore } });
  };

  private getMerchantOrderExchangeRate = (storeId: string, sourceOrderId: string) => {
    return this.client.get(`v1/merchant/${storeId}/exchange-rate/${sourceOrderId}`);
  };

  private provisionStore = (storeId: string) => {
    return this.client.post('v1/sops/provision', { storeId });
  };

  private createSop = (body: { storeId: string; title: string; content?: string }) => {
    return this.client.post<Sop>('v1/sops', body);
  };

  private importGorgiasSops = (storeId: string) => {
    return this.client.post<{ enqueued: boolean }>(`v1/sops/gorgias/import/${storeId}`, {});
  };

  private pullGorgiasTicketMessages = (storeId: string, channels: string[] = []) => {
    return this.client.post<{ enqueued: boolean }>(`v1/sops/gorgias/ticket-messages/${storeId}`, { channels });
  };

  private pullSlackMessages = (storeId: string, channelIds: string[] = []) => {
    return this.client.post<{ enqueued: boolean }>(`v1/sops/slack/messages/${storeId}`, { channelIds });
  };

  private getSlackSopSettings = (storeId: string) => {
    return this.client.get<SlackSopSettings>(`v1/sops/slack-settings/${storeId}`);
  };

  private updateSlackSopSettings = (
    storeId: string,
    settings: { cadence?: SopRefreshCadenceType; channelIds?: string[] },
  ) => {
    return this.client.patch<SlackSopSettings>(`v1/sops/slack-settings/${storeId}`, settings);
  };

  private getDistillInstructions = (storeId: string) => {
    return this.client.get<{ global: string | null; store: string | null }>(`v1/sops/distill-instructions/${storeId}`);
  };

  private getHelpDeskSettings = (storeId: string) => {
    return this.client.get<{ cadence: SopRefreshCadenceType; channels: string[] }>(
      `v1/sops/helpdesk-settings/${storeId}`,
    );
  };

  private updateHelpDeskSettings = (
    storeId: string,
    settings: { cadence?: SopRefreshCadenceType; channels?: string[] },
  ) => {
    return this.client.patch<{ cadence: SopRefreshCadenceType; channels: string[] }>(
      `v1/sops/helpdesk-settings/${storeId}`,
      settings,
    );
  };

  private listSopsByStoreId = (
    storeId: string,
    params?: { state?: SopStateType; fields?: string; latestContent?: boolean },
  ) => {
    return this.client.get<ListSopReturn[]>(`v1/sops/store/${storeId}`, { params });
  };

  private getSopById = (sopId: string) => {
    return this.client.get<Sop>(`v1/sops/${sopId}`);
  };

  private updateSopById = (sopId: string, body: { title: string; content: string }) => {
    return this.client.put<Sop>(`v1/sops/${sopId}`, body);
  };

  private updateSopState = (sopId: string, state: SopStateType) => {
    return this.client.patch(`v1/sops/${sopId}/state/${state}`);
  };

  private deleteSopById = (sopId: string) => {
    return this.client.delete(`v1/sops/${sopId}`);
  };

  private getWarrantySettings = (storeId: string) => {
    return this.client.get(`v1/stores/${storeId}/settings/warranty/in-store`);
  };

  private getWarranty = (storeId: string) => {
    return this.client.get(`v1/stores/${storeId}/settings/warranty`);
  };

  private updateWarrantySettings = (storeId: string, body: any) => {
    return this.client.put(`v1/stores/${storeId}/settings/warranty/in-store`, body);
  };

  private getOfflineClaimSettings = (storeId: string) => {
    return this.client.get<OfflineClaimSettings>(`v1/stores/${storeId}/settings/offline-claims`);
  };

  private updateOfflineClaimSettings = (storeId: string, body: Partial<OfflineClaimSettings>) => {
    return this.client.put<OfflineClaimSettings>(`v1/stores/${storeId}/settings/offline-claims`, body);
  };

  private createPriceRuleSet = (storeId: string, body: any) => {
    return this.client.post(`v1/quotes/${storeId}/ruleset`, body);
  };

  private updateDefaultWarrantyStatus = (storeId: string, body: any) => {
    return this.client.put(`v1/stores/${storeId}/settings/warranty/defaults`, body);
  };

  private updateWarranty = (storeId: string, warrantyId: string, body: any) => {
    return this.client.put(`v1/stores/${storeId}/settings/warranty/${warrantyId}`, body);
  };

  private cancelOrder = (storeId: string, sourceOrderId: string) => {
    return this.client.post(`v1/platforms/${storeId}/order/${sourceOrderId}/cancel`);
  };

  private deactivateGiftCard = (storeId: string, sourceGiftCardId: string) => {
    return this.client.delete(`v1/platforms/gift-card`, {
      params: {
        sourceGiftCardId,
        storeId,
      },
    });
  };

  private deactivateDiscountCode = (storeId: string, discountCodeId: string) => {
    return this.client.delete(`v1/platforms/discount-code`, {
      params: {
        discountCodeId,
        storeId,
      },
    });
  };
}
